import Zoom from '@/logic/Zoom';
import { LineName } from '@/formats/analysis-formats';
import type Audio from './Audio';
import Staff, { StaffType } from './Staff';
import type { Pixel } from './Unit';
import type Unit from './Unit';

const LINE_HEIGHT = 34;
const TOTAL_MARGIN = 0 + LINE_HEIGHT * 3;

export enum SourceType {
  Score = 'score',
  AudioImage = 'wave',
  Image3D = '3d',
  Grid = 'grid'
}

export interface Image3DRowData {
  staves: Staff[];
  y: Pixel;
  height: Pixel;
  end: Pixel;
  start: Pixel;
}

export interface Image3DPageData {
  image?: string;
  height: number;
  width: number
  rows: Array<Image3DRowData>;
}

interface SourceData {
  pixelUnit: Unit<Pixel>;
  width: Pixel;
  height: Pixel;
  imageUrl: string;
  type: SourceType;
  track?: Audio;
  license?: string;
  name: string | null;
  info: string;
  pages?: Array<Image3DPageData>;
  contributors?: Record<string, string>;
  refs?: Record<string, string>;
  source?: string;
  year?: string;
}

export default class Source implements Readonly<SourceData> {
  id = 0;

  readonly pixelUnit: Unit<Pixel>;

  readonly width: Pixel;

  readonly height: Pixel;

  imageUrl: string;

  readonly type: SourceType;

  readonly track?: Audio;

  readonly license?: string;

  readonly name: string | null;

  readonly info: string;

  readonly shouldSnap: boolean;

  readonly pages?: Array<Image3DPageData>;

  readonly contributors?: Record<string, string> | undefined;

  readonly refs?: Record<string, string>;

  readonly source?: string | undefined;

  readonly year?: string | undefined;

  zoom: Zoom;

  alreadyHasDefaultLines = false;

  protected constructor(parsed: SourceData) {
    this.pixelUnit = parsed.pixelUnit;
    this.width = parsed.width;
    this.height = parsed.height;
    this.imageUrl = parsed.imageUrl;
    this.type = parsed.type;
    this.track = parsed.track;
    this.name = parsed.name;
    this.info = parsed.info;
    this.pages = parsed.pages;
    this.shouldSnap = parsed.type !== 'wave';
    this.contributors = parsed.contributors;
    this.refs = parsed.refs;
    this.source = parsed.source;
    this.year = parsed.year;
    this.zoom = new Zoom();
    this.license = parsed.license;
  }

  setId(id: number): void {
    this.id = id;
  }

  setPage(index: number): void {
    if (!this.pages) return;
    const splittedImageUrl = this.imageUrl.split('/');
    splittedImageUrl.pop();
    this.imageUrl = `${splittedImageUrl.join('/')}/${this.pages[index].image}`;
  }

  setZoom(zoom: Zoom): void {
    this.zoom.exponent = zoom.getExponentForValue(zoom.value);
  }

  scaled(scale: number): Source {
    const data = { ...this };
    return new Source({
      ...data,
      width: data.width * scale,
      pixelUnit: data.pixelUnit.getScaled(scale),
    });
  }

  private getLine(id: number, name: LineName, top: number, height = LINE_HEIGHT): Staff {
    return new Staff(
      id,
      top as Pixel,
      (top + height) as Pixel,
      name,
      id ? StaffType.line : StaffType.bar,
    );
  }

  private getLines(): Staff[] {
    const bottomLineOffset = TOTAL_MARGIN + this.height;

    return [
      this.getLine(0, LineName.all, 0, this.height + LINE_HEIGHT * 6),
      this.getLine(1, LineName.top1, LINE_HEIGHT * 0),
      this.getLine(2, LineName.top2, LINE_HEIGHT * 1),
      this.getLine(3, LineName.top3, LINE_HEIGHT * 2),
      this.getLine(4, LineName.bot1, LINE_HEIGHT * 0 + bottomLineOffset),
      this.getLine(5, LineName.bot2, LINE_HEIGHT * 1 + bottomLineOffset),
      this.getLine(6, LineName.bot3, LINE_HEIGHT * 2 + bottomLineOffset),
    ];
  }

  addDefaultLines(): void {
    if (this.alreadyHasDefaultLines) return;
    const lines = this.getLines();
    const originalStaves = [
      ...this.pages![0].rows[0].staves.filter((staff) => staff.type === StaffType.staff),
    ];
    this.pages![0].rows[0].staves = [
      ...lines,
      ...originalStaves.map((staff, index) => ({
        ...staff,
        id: index + 7,
        top: (staff.top + TOTAL_MARGIN) as Pixel,
        bottom: (staff.bottom + TOTAL_MARGIN) as Pixel,
      })),
    ];
    this.pages![0].rows[0].height = (
      this.pages![0].rows[0].height as number + LINE_HEIGHT * 6
    ) as Pixel;
    this.alreadyHasDefaultLines = true;
  }

  get staves(): Staff[] {
    return this.pages![0].rows[0].staves;
  }
}
